import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Phone, PhoneCall, PhoneMissed, RefreshCw, Search, Settings, Star, ClipboardCheck, Smartphone, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const statusPill = (status) => {
  const base = 'text-[10px] px-2 py-0.5 rounded-full font-medium';
  if (status === 'ringing') return <span className={`${base} bg-amber-100 text-amber-800`}>RINGING</span>;
  if (status === 'answered') return <span className={`${base} bg-emerald-100 text-emerald-800`}>ANSWERED</span>;
  if (status === 'ended') return <span className={`${base} bg-slate-100 text-slate-700`}>ENDED</span>;
  if (status === 'missed') return <span className={`${base} bg-red-100 text-red-700`}>MISSED</span>;
  return <span className={`${base} bg-slate-100 text-slate-600`}>IDLE</span>;
};

const slaPill = (tag) => {
  const base = 'inline-block w-2.5 h-2.5 rounded-full mr-1';
  if (tag === 'green') return <span className={`${base} bg-emerald-500`} />;
  if (tag === 'yellow') return <span className={`${base} bg-amber-400`} />;
  if (tag === 'red') return <span className={`${base} bg-red-500`} />;
  return <span className={`${base} bg-slate-300`} />;
};

const fmtTs = (v) => v ? String(v).slice(0, 19).replace('T', ' ') : '—';
const fmtSec = (v) => (v === null || v === undefined) ? '—' : `${v}s`;

export default function CentreCallMonitor() {
  const { session } = useAuth();
  const [live, setLive] = useState(null);
  const [calls, setCalls] = useState([]);
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterCentre, setFilterCentre] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterHandler, setFilterHandler] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Dialogs
  const [showManual, setShowManual] = useState(false);
  const [showPhones, setShowPhones] = useState(false);
  const [showSla, setShowSla] = useState(false);
  const [showDevices, setShowDevices] = useState(false);
  const [reviewCall, setReviewCall] = useState(null);

  const post = useCallback(async (path, body) => {
    const res = await fetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: session?.token, ...body }),
    });
    const j = await res.json();
    if (!res.ok) throw new Error(j.detail || 'Request failed');
    return j;
  }, [session]);

  const refresh = useCallback(async () => {
    if (!session?.token) return;
    setLoading(true);
    try {
      const [liveRes, listRes, phonesRes] = await Promise.all([
        post('/api/call-monitor/live', {}),
        post('/api/call-monitor/list', {
          center: filterCentre, status: filterStatus,
          from_number: filterFrom || undefined,
          handled_by: filterHandler || undefined,
          date_from: filterDateFrom || undefined,
          date_to: filterDateTo || undefined,
          limit: 500,
        }),
        post('/api/call-monitor/phones/list', {}),
      ]);
      setLive(liveRes);
      setCalls(listRes.rows || []);
      setPhones(phonesRes.rows || []);
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  }, [session, post, filterCentre, filterStatus, filterFrom, filterHandler, filterDateFrom, filterDateTo]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh live tiles every 10s (dashboard-only, not the search table).
  useEffect(() => {
    if (!session?.token) return;
    const id = setInterval(() => {
      post('/api/call-monitor/live', {}).then(setLive).catch(() => {});
    }, 10000);
    return () => clearInterval(id);
  }, [session, post]);

  const canManage = session?.is_super_admin || session?.is_admin
    || session?.role_key === 'accountant' || session?.roles?.accounts
    || session?.roles?.mis || session?.roles?.reports;

  if (!canManage) {
    return <div className="p-8 text-center text-slate-500">Super Admin / Accounts access required.</div>;
  }

  const t = live?.totals || {};
  return (
    <div className="space-y-6" data-testid="call-monitor-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PhoneCall className="w-6 h-6 text-indigo-700" /> Centre Call Monitor
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Mystery-call any centre from any number. IntraPB will show who answered, how fast, and how long they spoke.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={refresh} disabled={loading} data-testid="cm-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowManual(true)} data-testid="cm-log-manual">
            <ClipboardCheck className="w-4 h-4 mr-1" /> Log Mystery Call
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowPhones(true)} data-testid="cm-phones">
            <Phone className="w-4 h-4 mr-1" /> Centre Phones
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowSla(true)} data-testid="cm-sla">
            <Settings className="w-4 h-4 mr-1" /> SLA
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowDevices(true)} data-testid="cm-devices">
            <Smartphone className="w-4 h-4 mr-1" /> Centre Devices
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="p-3"><p className="text-[10px] uppercase text-slate-500">Calls Today</p><p className="text-xl font-semibold" data-testid="cm-tile-total">{t.calls_today ?? 0}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] uppercase text-emerald-700">Answered</p><p className="text-xl font-semibold text-emerald-800" data-testid="cm-tile-answered">{t.answered_today ?? 0}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] uppercase text-red-700">Missed</p><p className="text-xl font-semibold text-red-700" data-testid="cm-tile-missed">{t.missed_today ?? 0}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] uppercase text-slate-500">Avg Answer</p><p className="text-xl font-semibold" data-testid="cm-tile-avg-answer">{t.avg_answer_secs ?? 0}s</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] uppercase text-slate-500">Avg Duration</p><p className="text-xl font-semibold" data-testid="cm-tile-avg-duration">{t.avg_duration_sec ?? 0}s</p></CardContent></Card>
      </div>

      {/* Live per-centre grid */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Live Centre Status</CardTitle>
          <p className="text-xs text-slate-500">
            SLA — <span className="inline-flex items-center">{slaPill('green')}Green ≤ {live?.sla?.sla_green_sec ?? 10}s</span> · <span className="inline-flex items-center">{slaPill('yellow')}Yellow ≤ {live?.sla?.sla_yellow_sec ?? 20}s</span> · <span className="inline-flex items-center">{slaPill('red')}Red missed or {'>'} {live?.sla?.sla_yellow_sec ?? 20}s</span>
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="text-xs">Centre</TableHead>
                <TableHead className="text-xs">Number</TableHead>
                <TableHead className="text-xs">Logged-in User</TableHead>
                <TableHead className="text-xs">Current</TableHead>
                <TableHead className="text-xs text-right">Calls Today</TableHead>
                <TableHead className="text-xs text-right">Missed</TableHead>
                <TableHead className="text-xs">Last Call</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(live?.centers || []).map(c => (
                <TableRow key={c.center} data-testid={`cm-live-${c.center}`}>
                  <TableCell className="text-xs font-medium">{c.center}</TableCell>
                  <TableCell className="text-xs">{c.phone || <em className="text-slate-400">not set</em>}</TableCell>
                  <TableCell className="text-xs">{c.current_handler || <em className="text-slate-400">nobody logged in</em>}</TableCell>
                  <TableCell className="text-xs">{statusPill(c.current_status)}{c.current_from ? <span className="ml-2 text-[10px] text-slate-500">from {c.current_from}</span> : null}</TableCell>
                  <TableCell className="text-xs text-right">{c.calls_today}</TableCell>
                  <TableCell className={`text-xs text-right ${c.missed_today > 0 ? 'text-red-700 font-semibold' : ''}`}>{c.missed_today}</TableCell>
                  <TableCell className="text-xs">
                    {c.last_call_at ? (
                      <span className="inline-flex items-center">{slaPill(c.last_call_sla)}{fmtTs(c.last_call_at)} · {c.last_call_status}{c.last_call_handler ? ` · ${c.last_call_handler}` : ''}</span>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
              {(live?.centers || []).length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6 text-xs">No centres configured. Add centre phone numbers first.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Search + call log */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Call Log</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
            <div>
              <Label className="text-xs">Centre</Label>
              <Select value={filterCentre} onValueChange={setFilterCentre}>
                <SelectTrigger data-testid="cm-filter-centre"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {(live?.centers || []).map(c => <SelectItem key={c.center} value={c.center}>{c.center}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="cm-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="ringing">Ringing</SelectItem>
                  <SelectItem value="answered">Answered</SelectItem>
                  <SelectItem value="ended">Ended</SelectItem>
                  <SelectItem value="missed">Missed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">From Number</Label><Input value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} placeholder="+91…" data-testid="cm-filter-from" /></div>
            <div><Label className="text-xs">Handled By</Label><Input value={filterHandler} onChange={(e) => setFilterHandler(e.target.value)} placeholder="name" data-testid="cm-filter-handler" /></div>
            <div><Label className="text-xs">From Date</Label><Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} data-testid="cm-filter-df" /></div>
            <div><Label className="text-xs">To Date</Label><Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} data-testid="cm-filter-dt" /></div>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto border border-slate-200 rounded">
            <Table>
              <TableHeader className="bg-slate-50 sticky top-0">
                <TableRow>
                  <TableHead className="text-xs">SLA</TableHead>
                  <TableHead className="text-xs">Ring At</TableHead>
                  <TableHead className="text-xs">Centre</TableHead>
                  <TableHead className="text-xs">From</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Answer In</TableHead>
                  <TableHead className="text-xs">Duration</TableHead>
                  <TableHead className="text-xs">Handled By</TableHead>
                  <TableHead className="text-xs">Source</TableHead>
                  <TableHead className="text-xs">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.map(c => (
                  <TableRow key={c.call_sid} data-testid={`cm-call-${c.call_sid}`}>
                    <TableCell>{slaPill(c.sla_tag)}</TableCell>
                    <TableCell className="text-xs">{fmtTs(c.ring_at)}</TableCell>
                    <TableCell className="text-xs">{c.center || '—'}</TableCell>
                    <TableCell className="text-xs">{c.from_number || '—'}</TableCell>
                    <TableCell className="text-xs">{statusPill(c.status)}</TableCell>
                    <TableCell className="text-xs">{fmtSec(c.answer_secs)}</TableCell>
                    <TableCell className="text-xs">{fmtSec(c.duration_sec)}</TableCell>
                    <TableCell className="text-xs">{c.handled_by || <em className="text-slate-400">—</em>}</TableCell>
                    <TableCell className="text-xs"><Badge variant="outline" className="text-[10px]">{c.source || 'webhook'}</Badge></TableCell>
                    <TableCell className="text-xs">
                      <Button size="sm" variant="outline" onClick={() => setReviewCall(c)} data-testid={`cm-review-${c.call_sid}`}>
                        <Star className="w-3 h-3 mr-1" /> Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {calls.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-6 text-xs">No calls match the filters.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ManualCallDialog open={showManual} onClose={() => { setShowManual(false); refresh(); }} centers={(live?.centers || []).map(c => c.center)} post={post} />
      <CentrePhonesDialog open={showPhones} onClose={() => { setShowPhones(false); refresh(); }} phones={phones} post={post} />
      <SlaDialog open={showSla} onClose={() => { setShowSla(false); refresh(); }} sla={live?.sla} post={post} />
      <CentreDevicesDialog open={showDevices} onClose={() => setShowDevices(false)} post={post} />
      <ReviewDialog call={reviewCall} onClose={() => { setReviewCall(null); refresh(); }} post={post} />
    </div>
  );
}

// ---------- Manual mystery-call log ----------
const ManualCallDialog = ({ open, onClose, centers, post }) => {
  const [center, setCenter] = useState('');
  const [from, setFrom] = useState('');
  const [answered, setAnswered] = useState(true);
  const [duration, setDuration] = useState(0);
  const [handledBy, setHandledBy] = useState('');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!center || !from) { toast.error('Centre + From number are required'); return; }
    setBusy(true);
    try {
      await post('/api/call-monitor/log-manual', {
        center, from_number: from, answered,
        duration_sec: Number(duration) || 0,
        handled_by: handledBy || undefined,
        remarks,
      });
      toast.success('Mystery call logged');
      setCenter(''); setFrom(''); setDuration(0); setHandledBy(''); setRemarks('');
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cm-manual-dialog">
        <DialogHeader><DialogTitle>Log Mystery Test Call</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Centre</Label>
            <Select value={center} onValueChange={setCenter}>
              <SelectTrigger data-testid="cm-manual-centre"><SelectValue placeholder="Select centre" /></SelectTrigger>
              <SelectContent>{centers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Called From (your test number)</Label>
            <Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="+91…" data-testid="cm-manual-from" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={answered} onCheckedChange={setAnswered} data-testid="cm-manual-answered" />
            <span className="text-xs">Answered?</span>
          </div>
          <div><Label className="text-xs">Call Duration (seconds)</Label>
            <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="cm-manual-duration" />
          </div>
          <div><Label className="text-xs">Handled By (optional — auto-detected from session if blank)</Label>
            <Input value={handledBy} onChange={(e) => setHandledBy(e.target.value)} data-testid="cm-manual-handler" />
          </div>
          <div><Label className="text-xs">Remarks</Label>
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="cm-manual-remarks" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-indigo-600 text-white" data-testid="cm-manual-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Centre phone configuration ----------
const CentrePhonesDialog = ({ open, onClose, phones, post }) => {
  const [center, setCenter] = useState('');
  const [phone, setPhone] = useState('');

  const save = async () => {
    if (!center || !phone) { toast.error('Centre + phone required'); return; }
    try {
      await post('/api/call-monitor/phones/set', { center, phone, active: true });
      toast.success('Saved');
      setCenter(''); setPhone('');
      onClose();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cm-phones-dialog">
        <DialogHeader><DialogTitle>Centre Phone Numbers</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {phones.map(p => (
            <div key={p.center} className="flex items-center justify-between text-xs border-b border-slate-100 py-1">
              <span className="font-medium">{p.center}</span>
              <span>{p.phone}</span>
              <Badge className={p.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}>{p.active ? 'active' : 'inactive'}</Badge>
            </div>
          ))}
          {phones.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No centre phones configured yet.</p>}
        </div>
        <div className="grid grid-cols-2 gap-2 pt-3 border-t">
          <div><Label className="text-xs">Centre Code</Label><Input value={center} onChange={(e) => setCenter(e.target.value.toUpperCase())} placeholder="PB-HSR" data-testid="cm-phone-centre" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 85500 78515" data-testid="cm-phone-num" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} className="bg-indigo-600 text-white" data-testid="cm-phone-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- SLA config ----------
const SlaDialog = ({ open, onClose, sla, post }) => {
  const [green, setGreen] = useState(sla?.sla_green_sec ?? 10);
  const [yellow, setYellow] = useState(sla?.sla_yellow_sec ?? 20);
  const [missed, setMissed] = useState(sla?.missed_sec ?? 20);
  useEffect(() => { if (sla) { setGreen(sla.sla_green_sec); setYellow(sla.sla_yellow_sec); setMissed(sla.missed_sec); } }, [sla]);

  const save = async () => {
    try {
      await post('/api/call-monitor/sla', {
        sla_green_sec: Number(green), sla_yellow_sec: Number(yellow), missed_sec: Number(missed),
      });
      toast.success('SLA updated');
      onClose();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cm-sla-dialog">
        <DialogHeader><DialogTitle>Answer-Time SLA</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Green if answered within (seconds)</Label>
            <Input type="number" value={green} onChange={(e) => setGreen(e.target.value)} data-testid="cm-sla-green" /></div>
          <div><Label className="text-xs">Yellow if answered within (seconds)</Label>
            <Input type="number" value={yellow} onChange={(e) => setYellow(e.target.value)} data-testid="cm-sla-yellow" /></div>
          <div><Label className="text-xs">Auto-missed after (seconds)</Label>
            <Input type="number" value={missed} onChange={(e) => setMissed(e.target.value)} data-testid="cm-sla-missed" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-indigo-600 text-white" data-testid="cm-sla-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Review dialog ----------
const ReviewDialog = ({ call, onClose, post }) => {
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({
    greeting_used: false, correct_info: false, booking_captured: false,
    price_discussed: false, followup_needed: false,
    call_quality: 3, remarks: '',
  });

  useEffect(() => {
    if (!call) return;
    post('/api/call-monitor/review/get', { call_sid: call.call_sid })
      .then(j => {
        if (j.review) { setExisting(j.review); setForm(prev => ({ ...prev, ...j.review })); }
        else setExisting(null);
      }).catch(() => {});
  }, [call, post]);

  if (!call) return null;

  const save = async () => {
    try {
      await post('/api/call-monitor/review', { call_sid: call.call_sid, ...form });
      toast.success('Review saved');
      onClose();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={!!call} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="cm-review-dialog">
        <DialogHeader>
          <DialogTitle>Call Quality Review</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-600 bg-slate-50 rounded p-2">
            <p><strong>{call.center}</strong> · from {call.from_number} · {fmtTs(call.ring_at)}</p>
            <p>Handled by {call.handled_by || <em>—</em>} · answered in {fmtSec(call.answer_secs)} · duration {fmtSec(call.duration_sec)}</p>
            {call.recording_url && <p className="mt-1"><a href={call.recording_url} target="_blank" rel="noreferrer" className="text-indigo-700 underline">Play recording</a></p>}
            {call.transcript && <details className="mt-1"><summary className="cursor-pointer">Transcript</summary><pre className="text-[11px] whitespace-pre-wrap mt-2">{call.transcript}</pre></details>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              ['greeting_used', 'Greeting Used'],
              ['correct_info', 'Correct Information Given'],
              ['booking_captured', 'Booking Captured'],
              ['price_discussed', 'Price Discussed'],
              ['followup_needed', 'Follow-up Needed'],
            ].map(([name, label]) => (
              <div key={name} className="flex items-center gap-2">
                <Switch checked={!!form[name]} onCheckedChange={(v) => setForm({ ...form, [name]: v })} data-testid={`cm-rv-${name}`} />
                <span className="text-xs">{label}</span>
              </div>
            ))}
          </div>
          <div>
            <Label className="text-xs">Call Quality (1–5)</Label>
            <Input type="number" min={1} max={5} value={form.call_quality} onChange={(e) => setForm({ ...form, call_quality: Number(e.target.value) })} data-testid="cm-rv-quality" />
          </div>
          <div>
            <Label className="text-xs">Remarks</Label>
            <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="cm-rv-remarks" />
          </div>
          {existing && <p className="text-[10px] text-slate-400">Last reviewed by {existing.reviewed_by} on {fmtTs(existing.reviewed_at)}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} className="bg-indigo-600 text-white" data-testid="cm-rv-save">Save Review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Centre Devices (Android companion app management) ----------
const CentreDevicesDialog = ({ open, onClose, post }) => {
  const [rows, setRows] = useState([]);
  const [centers, setCenters] = useState([]);
  const [activateCentre, setActivateCentre] = useState('PB-HSR');
  const [activateLabel, setActivateLabel] = useState('');
  const [issuedCode, setIssuedCode] = useState(null);

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const [devices, live] = await Promise.all([
        post('/api/call-monitor/devices/list', {}),
        post('/api/call-monitor/live', {}),
      ]);
      setRows(devices.rows || []);
      setCenters((live.centers || []).map(c => c.center));
    } catch { setRows([]); setCenters([]); }
  }, [open, post]);

  useEffect(() => { load(); }, [load]);

  const activate = async () => {
    if (!activateCentre) { toast.error('Centre required'); return; }
    try {
      const j = await post('/api/call-monitor/devices/activate', {
        center: activateCentre, label: activateLabel, ttl_hours: 48,
      });
      setIssuedCode(j.activation_code);
      toast.success(`Code ready — share with the device installer within 48h`);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const revoke = async (device_id) => {
    if (!window.confirm(`Revoke device ${device_id}? The phone will stop syncing.`)) return;
    try {
      await post('/api/call-monitor/devices/revoke', { device_id });
      toast.success('Revoked');
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const replace = async (device_id) => {
    try {
      const j = await post('/api/call-monitor/devices/replace', { device_id });
      setIssuedCode(j.activation_code);
      toast.success('Old device revoked. Use the new activation code on the replacement phone.');
      await load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="cm-devices-dialog">
        <DialogHeader><DialogTitle>Centre Devices (Android Companion)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {/* Download the Android project */}
          <div className="flex items-center justify-between border border-indigo-200 bg-indigo-50 rounded p-2 text-xs">
            <span>Need the Android app? Download the source project and build in Android Studio.</span>
            <a
              href={`${window.location.origin}/PurnabramhaCallCompanion.zip`}
              download
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-600 text-white text-xs"
              data-testid="cm-dev-download"
            >
              <Download className="w-3 h-3" /> Download ZIP
            </a>
          </div>
          {/* Activate a new device */}
          <div className="border border-slate-200 rounded p-3 bg-slate-50">
            <p className="text-xs font-semibold mb-2">Activate New Device</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Centre</Label>
                <Select value={activateCentre} onValueChange={setActivateCentre}>
                  <SelectTrigger data-testid="cm-dev-centre"><SelectValue placeholder="Select centre" /></SelectTrigger>
                  <SelectContent>
                    {(centers.length ? centers : ['PB-HSR']).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Label</Label>
                <Input value={activateLabel} onChange={(e) => setActivateLabel(e.target.value)} placeholder="Airtel HSR phone" data-testid="cm-dev-label" />
              </div>
              <div className="flex items-end">
                <Button onClick={activate} className="bg-indigo-600 text-white" data-testid="cm-dev-activate">Generate Code</Button>
              </div>
            </div>
            {issuedCode && (
              <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded" data-testid="cm-dev-code">
                <p className="text-xs">Activation code (valid 48h):</p>
                <p className="text-xl font-mono font-bold tracking-wide">{issuedCode}</p>
                <p className="text-[10px] text-slate-500 mt-1">Type this exactly into the Android companion app on the centre phone.</p>
              </div>
            )}
          </div>

          {/* Existing devices */}
          <div className="overflow-x-auto border border-slate-200 rounded max-h-72 overflow-y-auto">
            <Table>
              <TableHeader className="bg-slate-50 sticky top-0">
                <TableRow>
                  <TableHead className="text-xs">Centre</TableHead>
                  <TableHead className="text-xs">Device ID</TableHead>
                  <TableHead className="text-xs">Phone</TableHead>
                  <TableHead className="text-xs">Version</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Last Heartbeat</TableHead>
                  <TableHead className="text-xs">Permissions</TableHead>
                  <TableHead className="text-xs">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.device_id} data-testid={`cm-dev-row-${r.device_id}`}>
                    <TableCell className="text-xs font-medium">{r.center}</TableCell>
                    <TableCell className="text-xs font-mono">{r.device_id.slice(0, 12)}…</TableCell>
                    <TableCell className="text-xs">{r.phone_model || '—'}<br /><span className="text-[10px] text-slate-500">{r.label}</span></TableCell>
                    <TableCell className="text-xs">{r.app_version || '—'}<br /><span className="text-[10px] text-slate-500">Android {r.android_version}</span></TableCell>
                    <TableCell className="text-xs">
                      {r.revoked
                        ? <Badge className="bg-red-100 text-red-700">Revoked</Badge>
                        : r.online
                          ? <Badge className="bg-emerald-100 text-emerald-700">Online</Badge>
                          : <Badge className="bg-amber-100 text-amber-700">Offline</Badge>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.last_seen_at ? fmtTs(r.last_seen_at) : '—'}
                      {typeof r.seconds_since_seen === 'number' && (
                        <><br /><span className="text-[10px] text-slate-500">{r.seconds_since_seen < 60 ? `${r.seconds_since_seen}s ago` : `${Math.floor(r.seconds_since_seen / 60)}m ago`} · battery {r.battery_pct ?? '—'}%</span></>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="flex flex-wrap gap-0.5 max-w-[220px]">
                        {Object.entries(r.permissions || {}).map(([k, v]) => (
                          <span key={k} className={`text-[9px] px-1 rounded ${v === 'granted' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'}`} title={k}>{k.replace('ACCESS_', '').replace('_', ' ').slice(0, 8)}</span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {!r.revoked && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => replace(r.device_id)} className="mr-1" data-testid={`cm-dev-replace-${r.device_id}`}>Replace</Button>
                          <Button size="sm" variant="outline" onClick={() => revoke(r.device_id)} className="text-red-700" data-testid={`cm-dev-revoke-${r.device_id}`}>Revoke</Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-4 text-xs">No devices provisioned yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
